// Client-side storage for ad-hoc RTSP camera entries added in Local Mode.
//
// Camera metadata (name/host/port/path) is stored as plain JSON in
// localStorage. Credentials (username/password) are AES-GCM encrypted
// before they touch localStorage; the AES key itself is a
// non-extractable CryptoKey held in IndexedDB, so a raw dump/backup of
// localStorage alone cannot recover a password — the key material never
// exists in an exportable form. This does NOT protect against an
// attacker who can already run JS in this origin (XSS): no storage that
// client-side JS can read is safe against code running in that same JS.
//
// SubtleCrypto (and crypto.randomUUID) only exist in a "secure context"
// (HTTPS, or localhost) — Local Mode is meant to be reached over plain
// http://<lan-ip> too, where crypto.subtle is undefined. isEncryptionAvailable()
// lets callers detect that and fall back to not remembering credentials
// rather than either crashing or silently writing them out as plaintext.

import { apiFetch } from './streamApi';

const DB_NAME  = 'prvsk_local_mode';
const DB_STORE = 'keys';
const KEY_ID   = 'cred_key';
const CAMS_KEY = 'prvsk_local_cameras';

export function isEncryptionAvailable() {
  return typeof crypto !== 'undefined' && !!crypto.subtle && typeof crypto.subtle.encrypt === 'function';
}

// crypto.randomUUID() needs a secure context; crypto.getRandomValues() does not.
export function uuid() {
  if (typeof crypto.randomUUID === 'function') {
    try { return crypto.randomUUID(); } catch { /* fall through to manual v4 */ }
  }
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, x => x.toString(16).padStart(2, '0'));
  return `${hex.slice(0,4).join('')}-${hex.slice(4,6).join('')}-${hex.slice(6,8).join('')}-${hex.slice(8,10).join('')}-${hex.slice(10,16).join('')}`;
}

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const req = db.transaction(DB_STORE, 'readonly').objectStore(DB_STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

function idbSet(db, key, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

let _keyPromise = null;
function getKey() {
  if (!_keyPromise) {
    _keyPromise = (async () => {
      const db = await openDb();
      let key = await idbGet(db, KEY_ID);
      if (!key) {
        key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
        await idbSet(db, KEY_ID, key);
      }
      return key;
    })();
  }
  return _keyPromise;
}

const toB64   = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromB64 = (b64) => Uint8Array.from(atob(b64), c => c.charCodeAt(0));

async function encryptJson(obj) {
  const key  = await getKey();
  const iv   = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(obj));
  const ct   = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  return { iv: toB64(iv), ct: toB64(ct) };
}

async function decryptJson({ iv, ct }) {
  const key  = await getKey();
  const data = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64(iv) }, key, fromB64(ct));
  return JSON.parse(new TextDecoder().decode(data));
}

function readCams() {
  try { return JSON.parse(localStorage.getItem(CAMS_KEY)) || []; } catch { return []; }
}
function writeCams(cams) { localStorage.setItem(CAMS_KEY, JSON.stringify(cams)); }

// Camera list without credential ciphertext — safe to render directly.
export function listCameras() {
  return readCams().map(({ cred, ...meta }) => ({ ...meta, hasSavedCredentials: !!cred }));
}

// Returns { id, remembered } — remembered is false if the caller asked to
// remember credentials but this context can't encrypt them (see
// isEncryptionAvailable above), so callers can warn the user instead of
// assuming the save silently worked.
export async function saveCamera({ id, name, protocol, host, port, path, transport, username, password, remember }) {
  const cams = readCams();
  const camId = id || uuid();
  const entry = { id: camId, name, protocol, host, port, path, transport };
  const canRemember = remember && (username || password) && isEncryptionAvailable();
  if (canRemember) {
    entry.cred = await encryptJson({ username, password });
  }
  const idx = cams.findIndex(c => c.id === camId);
  if (idx >= 0) cams[idx] = entry; else cams.push(entry);
  writeCams(cams);
  return { id: camId, remembered: !!entry.cred };
}

export async function getCredentials(id) {
  const cam = readCams().find(c => c.id === id);
  if (!cam?.cred) return { username: '', password: '' };
  return decryptJson(cam.cred);
}

export function removeCamera(id) {
  writeCams(readCams().filter(c => c.id !== id));
}

export async function clearAll() {
  localStorage.removeItem(CAMS_KEY);
  _keyPromise = null;
  await new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}

// rtsp_url carries the port/path (host comes from cam.ip, the field the
// server keeps authoritative and unmasked); parsing failures just fall back
// to the RTSP default port and an empty path.
function portAndPathFromRtspUrl(rtspUrl) {
  if (!rtspUrl) return { port: '554', path: '' };
  try {
    const u = new URL(rtspUrl);
    return { port: u.port || '554', path: u.pathname + u.search };
  } catch {
    return { port: '554', path: '' };
  }
}

// Pulls the account's server-side camera list into the Local Mode camera
// store on login, so cameras configured in the main application are ready to
// view here with no manual RTSP/credential entry. Dedup key is the camera's
// IP — a camera already saved locally under that IP (manually added, or from
// a previous sync) is left untouched, so this never clobbers a locally-set
// password. Reads `/cameras` (not `/ui/cameras`, which masks the password
// for display) — same auth (`_verify_token`), unmasked, already how
// parvekshak_api.py exposes it (see parvekshak_api.py:1584) — so this needed
// no new backend endpoint. Credentials are encrypted the same way a
// manually-entered "remember" does (isEncryptionAvailable() gates it); over
// insecure Local Mode http they're simply not remembered, same fallback as
// today. Best-effort: any failure (offline, server error, no token) is
// swallowed so it never blocks login.
export async function syncServerCamerasToLocal() {
  let payload;
  try {
    const res = await apiFetch('/cameras');
    if (!res.ok) return { added: 0, skipped: 0 };
    payload = await res.json();
  } catch {
    return { added: 0, skipped: 0 };
  }

  const cams = readCams();
  const existingIps = new Set(cams.map(c => c.host));
  let added = 0, skipped = 0;

  for (const cam of (payload?.cameras || [])) {
    const ip = cam.ip;
    if (!ip) continue;
    if (existingIps.has(ip)) { skipped++; continue; }
    const { port, path } = portAndPathFromRtspUrl(cam.rtsp_url);
    const entry = {
      id: uuid(),
      name: cam.name || ip,
      protocol: 'rtsp',
      host: ip,
      port,
      path,
      transport: cam.rtsp_transport === 'udp' ? 'udp' : 'tcp',
      source: 'server-sync',
    };
    if ((cam.user || cam.password) && isEncryptionAvailable()) {
      entry.cred = await encryptJson({ username: cam.user || '', password: cam.password || '' });
    }
    cams.push(entry);
    existingIps.add(ip);
    added++;
  }

  writeCams(cams);
  return { added, skipped };
}
